/**
 * API service for calling Cloud Functions
 */

import { httpsCallable } from "firebase/functions";
import { functions, auth } from "./firebase";

// Helper to get auth token
const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
};

// Check if using emulators
// Create .env.local with REACT_APP_USE_EMULATOR=true to enable
const useEmulator = process.env.REACT_APP_USE_EMULATOR === "true";

// Get project ID from environment
const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "supernews-273a7";

// Call onRequest functions (using URL)
const callRequestFunction = async (
  functionName: string,
  data?: unknown
): Promise<unknown> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("User must be authenticated");
  }

  // Use emulator URL in development, production URL otherwise
  const url = useEmulator
    ? `http://localhost:5001/${projectId}/us-central1/${functionName}`
    : `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(data || {}),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Request failed");
  }

  return response.json();
};

// Call onCall functions (using SDK)
const callCallFunction = (functionName: string) => {
  return httpsCallable(functions, functionName);
};

// Call public onRequest functions (no auth required)
const callPublicRequestFunction = async (
  functionNameWithParams: string,
  headers?: Record<string, string>
): Promise<Response> => {
  // Split function name and query params
  const [functionName, queryParams] = functionNameWithParams.split('?');

  // Use emulator URL in development, production URL otherwise
  const url = useEmulator
    ? `http://localhost:5001/${projectId}/us-central1/${functionName}${queryParams ? '?' + queryParams : ''}`
    : `https://us-central1-${projectId}.cloudfunctions.net/${functionName}${queryParams ? '?' + queryParams : ''}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  return response;
};

// API functions
export const api = {
  // Auth
  authUser: async () => {
    const response = await callRequestFunction("authUser");
    return response as {
      success: boolean;
      user?: {
        uid: string;
        email: string;
        role?: string;
        status: string;
        displayName?: string;
        photoURL?: string;
        requestedRole?: string;
      };
      error?: string;
      requiresRegistration?: boolean;
    };
  },

  // Access management
  requestAccess: async (data: {
    firstName: string;
    lastName: string;
    mobile: string;
    address: string;
    gender: "male" | "female" | "other" | "prefer-not-to-say";
    education: string;
    experience?: string;
    requestedRole: "reporter" | "co-editor" | "chief-editor";
  }) => {
    const callFunction = callCallFunction("requestAccess");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      userId?: string;
      error?: string;
    };
  },

  listUsers: async (status?: "pending" | "approved" | "rejected") => {
    const callFunction = callCallFunction("listUsers");
    const result = await callFunction({ status });
    return result.data as {
      success: boolean;
      users?: Array<{
        uid: string;
        email: string;
        displayName?: string;
        firstName?: string;
        lastName?: string;
        mobile?: string;
        status: string;
        role?: string;
        requestedRole?: string;
        requestedAt?: string | null;
        createdAt: string;
      }>;
      error?: string;
    };
  },

  manageUsers: async (data: {
    userId: string;
    action: "approve" | "reject";
    role?: "reporter" | "co-editor" | "chief-editor" | "admin";
    rejectionReason?: string;
  }) => {
    const callFunction = callCallFunction("manageUsers");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      error?: string;
    };
  },

  // News management
  submitNews: async (data: {
    title: string;
    subtitle: string;
    content: string;
    images?: string[];
    videoLinks?: string[];
  }) => {
    const callFunction = callCallFunction("submitNews");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      newsId?: string;
      error?: string;
    };
  },

  searchNews: async (data?: {
    status?: string;
    authorUid?: string;
    limit?: number;
    orderBy?: "createdAt" | "publishedAt";
    orderDirection?: "asc" | "desc";
  }) => {
    const callFunction = callCallFunction("searchNews");
    const result = await callFunction(data || {});
    return result.data as {
      success: boolean;
      news?: Array<{
        id: string;
        title: string;
        subtitle: string;
        content: string;
        images?: string[];
        videoLinks?: string[];
        status: string;
        authorUid: string;
        createdAt: any;
        updatedAt: any;
        publishedAt?: any;
      }>;
      error?: string;
    };
  },

  updateNews: async (data: {
    newsId: string;
    title?: string;
    subtitle?: string;
    content?: string;
    images?: string[];
    videoLinks?: string[];
    status?: string;
  }) => {
    const callFunction = callCallFunction("updateNews");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      error?: string;
    };
  },

  editNews: async (data: {
    newsId: string;
    title?: string;
    subtitle?: string;
    content?: string;
    images?: string[];
    videoLinks?: string[];
  }) => {
    const callFunction = callCallFunction("editNews");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      editId?: string;
      isNewEdit?: boolean;
      error?: string;
    };
  },

  approveNews: async (data: {
    newsId: string;
    approve: boolean;
    feedback?: string;
  }) => {
    const callFunction = callCallFunction("approveNews");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      error?: string;
    };
  },

  deleteNews: async (data: { newsId: string }) => {
    const callFunction = callCallFunction("deleteNews");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      error?: string;
    };
  },

  publishNews: async (data: { newsId: string }) => {
    const callFunction = callCallFunction("publishNews");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      error?: string;
    };
  },

  // Media upload
  uploadImage: async (data: { imageData: string; fileName?: string }) => {
    const callFunction = callCallFunction("uploadImage");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      url?: string;
      error?: string;
    };
  },

  // Heartbeat - Get role-based dashboard statistics
  heartbeat: async () => {
    const callFunction = callCallFunction("heartbeat");
    const result = await callFunction({});
    return result.data as {
      success: boolean;
      data?: {
        draftsCount: number;
        inReviewCount: number;
        approvedCount?: number;
        publishedCount?: number;
        pendingUsersCount?: number;
        userRole: string;
        userStatus: string;
      };
      error?: string;
    };
  },

  // PUBLIC API - No authentication required

  // Get single published article by ID
  getPublishedArticle: async (newsId: string, cachedETag?: string) => {
    try {
      const headers: Record<string, string> = {};

      // If we have a cached ETag, send If-None-Match header
      if (cachedETag) {
        headers["If-None-Match"] = cachedETag;
      }

      const response = await callPublicRequestFunction(
        `getPublishedArticle?id=${encodeURIComponent(newsId)}`,
        headers
      );

      // 304 Not Modified
      if (response.status === 304) {
        return {
          success: true,
          modified: false,
          article: null, // Caller should use cached article
        };
      }

      // 429 Too Many Requests
      if (response.status === 429) {
        const data = await response.json();
        return {
          success: false,
          error: data.error || "Too many requests. Please try again later.",
          retryAfter: data.retryAfter,
        };
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Store ETag for future requests
      const etag = response.headers.get("ETag");

      return {
        success: true,
        modified: true,
        article: data.article,
        etag,
      };
    } catch (error: unknown) {
      const err = error as { message?: string };
      return {
        success: false,
        error: err.message || "Failed to fetch article",
      };
    }
  },

  // Get public homepage (pinned articles first, then published articles)
  getPublicHomepage: async (page: number = 1, pageSize: number = 12, cachedVersion?: number) => {
    try {
      const headers: Record<string, string> = {};

      // If we have a cached version, send If-None-Match header
      if (cachedVersion !== undefined) {
        headers["If-None-Match"] = `"${cachedVersion}"`;
      }

      const response = await callPublicRequestFunction(
        `getPublicHomepage?page=${page}&pageSize=${pageSize}`,
        headers
      );

      // 304 Not Modified - use cached data
      if (response.status === 304) {
        return {
          success: true,
          modified: false,
          articles: [],
          pinnedCount: 0,
          totalCount: 0,
          page,
          pageSize,
          totalPages: 1,
          version: cachedVersion,
        };
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Get ETag for future requests
      const etag = response.headers.get("ETag");
      const version = etag ? parseInt(etag.replace(/"/g, "")) : data.version || 0;

      return {
        success: true,
        modified: true,
        articles: data.articles || [],
        pinnedCount: data.pinnedCount || 0,
        totalCount: data.totalCount || 0,
        page: data.page || 1,
        pageSize: data.pageSize || 12,
        totalPages: data.totalPages || 1,
        version,
      };
    } catch (error: unknown) {
      const err = error as { message?: string };
      return {
        success: false,
        error: err.message || "Failed to fetch homepage",
        articles: [],
        pinnedCount: 0,
        totalCount: 0,
        page: 1,
        pageSize: 12,
        totalPages: 1,
      };
    }
  },

  // Homepage Layout Management
  getHomepageLayout: async () => {
    const callFunction = callCallFunction("getHomepageLayout");
    const result = await callFunction({});
    return result.data as {
      success: boolean;
      layout?: {
        id?: string;
        pinnedArticleIds: string[];
        version: number;
        updatedBy: string;
        updatedAt: any;
      };
      validation?: Array<{
        id: string;
        valid: boolean;
        reason?: string;
      }>;
      error?: string;
    };
  },

  updateHomepageLayout: async (data: { pinnedArticleIds: string[] }) => {
    const callFunction = callCallFunction("updateHomepageLayout");
    const result = await callFunction(data);
    return result.data as {
      success: boolean;
      version?: number;
      error?: string;
    };
  },

  // Archive Management
  archiveNews: async (newsId: string) => {
    const callFunction = callCallFunction("archiveNews");
    const result = await callFunction({ newsId });
    return result.data as {
      success: boolean;
      error?: string;
    };
  },

  getArchivedNews: async () => {
    const callFunction = callCallFunction("getArchivedNews");
    const result = await callFunction({});
    return result.data as {
      success: boolean;
      articles?: Array<{
        id: string;
        title: string;
        subtitle?: string;
        authorId: string;
        status: string;
        publishedAt?: any;
        archivedAt: any;
        archivedBy: string;
        originalId: string;
      }>;
      error?: string;
    };
  },
};

